import { enc } from 'crypto-js'
import { isDev, appName } from '@/utils'

const { Utf8, Base64 } = enc

/**
 * 判断是否需要加密
 */
export const needCrypt = (isCrypt?: boolean): boolean => {
  return isCrypt ?? !isDev
}

/**
 * 将值转换为字符串
 * @param val - 任意类型的值
 * @returns 字符串形式的值
 */
const stringify = (val: unknown): string => {
  return typeof val === 'string' ? val : JSON.stringify(val)
}

/**
 * 将值进行 Base64 编码序列化
 * @param val - 需要序列化的值
 * @returns Base64 编码后的字符串
 */
export const serialize = (val: string): string => {
  const strVal = stringify(val)
  return Base64.stringify(Utf8.parse(strVal))
}

/**
 * 尝试对 Base64 编码的字符串进行解码
 * @param val - Base64 编码的字符串
 * @returns 解码后的字符串，解码失败则返回 null
 */
const decodeBase64 = (val: string): string | null => {
  try {
    return Base64.parse(val).toString(Utf8)
  } catch {
    // Base64 解码失败，返回 null 表示非 Base64 编码
    return null
  }
}

/**
 * 尝试将字符串解析为 JSON 对象
 * @param val - JSON 字符串
 * @returns 解析后的对象，解析失败则返回原始字符串
 */
const parseJSON = <T = unknown>(val: string): T | string => {
  try {
    return JSON.parse(val) as T
  } catch {
    // JSON 解析失败，返回原始字符串
    return val
  }
}

/**
 * 反序列化存储的值
 * @param val - 需要反序列化的值（通常为 Base64 编码的字符串）
 * @returns 反序列化后的值，无效输入返回 undefined
 */
export const deserialize = <T = string>(val: unknown): T | undefined => {
  // 非字符串类型无法反序列化
  if (typeof val !== 'string' || val === '') {
    return undefined
  }

  // 尝试 Base64 解码
  const decodedVal = decodeBase64(val)

  // 如果解码失败，说明不是加密存储的数据，尝试直接解析原始值
  const targetVal = decodedVal ?? val

  // 解析 JSON 或返回原始字符串
  const result = parseJSON<T>(targetVal)

  // 空字符串视为无效值
  return result === '' ? undefined : (result as T | undefined)
}

/**
 * 生成带应用前缀的缓存键名
 * @param name - 原始键名
 * @returns 格式化后的键名：__appName__name__
 */
export const getName = (name: string): string => {
  return `__${appName}__${name}__`
}
